/**
 * The people on the About page. The bundled five are the offline sample; once
 * the CMS has answered the list is replaced with the real team, in two groups
 * the page shows as tabs.
 */

export type TeamGroup = 'The Board' | 'Team Members';

export const TEAM_GROUPS: TeamGroup[] = ['The Board', 'Team Members'];

export interface TeamMember {
    first: string;
    last: string;
    name: string;
    role: string;
    /** Portrait URL; empty until one is uploaded — the card then shows initials. */
    photo: string;
    /** Bio paragraphs; empty until someone writes them. */
    bio: string[];
    group: TeamGroup;
}

/** "Nelson Olanipekun" → "Nelson" + "Olanipekun"; a single word is all last name. */
export function nameParts(name: string): { first: string; last: string } {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { first: '', last: words[0] ?? '' };
    return { first: words[0], last: words.slice(1).join(' ') };
}

export const initialsOf = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');

const sample = (name: string, role: string, photo: string, bio: string[]): TeamMember => ({
    ...nameParts(name),
    name,
    role,
    photo,
    bio,
    group: 'Team Members',
});

export const TEAM: TeamMember[] = [
    sample('Nelson Olanipekun', 'Founder & Team Lead', '/about/team-portrait.webp', [
        'Nelson founded Citizens’ Gavel on the conviction that Nigeria’s justice problem is less about the law on the books than about the months a case can sit untouched before anyone acts on it.',
        'He sets the organisation’s direction across legal practice, research and product, and keeps its work anchored to a single question: did a real case move, and did a real person get their life back?',
    ]),
    sample('John Doe', 'Legal Associate', '/about/team-1.webp', [
        'Carries the case files. Reviews incoming legal-aid requests, checks eligibility, and gets each accepted matter in front of a solicitor who can act on it quickly.',
        'Appears in magistrate courts and police stations across the pilot states, and stays with a matter until it is heard, withdrawn or the person is released.',
    ]),
    sample('Jane Smith', 'Programme Manager', '/about/team-2.webp', [
        'Runs the programmes that turn a good idea into something that works in twelve states at once — staffing, partner coordination, training and the unglamorous logistics in between.',
        'Works closely with community paralegals and partner organisations to make sure a referral into formal legal help actually completes rather than quietly stalling.',
    ]),
    sample('Ada Okafor', 'Research Lead', '/about/team-3.webp', [
        'Leads the research behind the Justice Delivery Index — case-file sampling, freedom-of-information requests, and the methodology that makes court performance comparable across states.',
        'Insists on publishing gaps rather than estimates: where a court declines to release its records, that refusal is reported as a finding in its own right.',
    ]),
    sample('Musa Bello', 'Legal Counsel', '/about/team-4.webp', [
        'Advises on the organisation’s own legal position and on the strategic cases it takes forward, from detention challenges to freedom-of-information litigation.',
        'Reviews the legal standards built into Podus, so that automated guidance stays within what the law actually supports — and says plainly when a question needs a human lawyer.',
    ]),
];

/** Members of one group, in page order. */
export const teamIn = (group: TeamGroup): TeamMember[] => TEAM.filter((m) => m.group === group);
